import { useCallback, useEffect, useRef, useState } from "react";
import { streamChat } from "./streamChat";
import { geturl } from "../../common/api/SafeClient";
import { useChatDemoFlag } from "../demo/demoFlags";
import { ASSISTANT_DEMO_EXCHANGES } from "./assistantDemoScript";
import type { ChatMessage } from "./types";

let idCounter = 0;
const nextId = () => `ac-${++idCounter}`;

export interface AssistantChatContext {
    /** Project being discussed; enables analysis grounding on the backend. */
    projectId?: number;
    analysisVersions?: number[];
    /** Serialized analysis output the assistant should ground its answers in. */
    analysisContent?: string;
    /** Short description of the page the user is on (sent to the backend). */
    pageDescription?: string;
    /** Page name recorded alongside feedback. */
    pageName?: string;
    /**
     * Which assistant to talk to. "coach" swaps the backend system prompt for
     * the Negotiation Coach, which also runs practice negotiations. Fixed for
     * the life of the hook: the drawer keeps one conversation per mode.
     */
    mode?: "ask" | "coach";
}

// Resolved through geturl at call time so the requests go to the API host,
// where the session cookie lives. A bare path would resolve against the SPA
// origin and arrive without credentials (401), see getAttachmentDownloadUrl.
// Call time, not module load: the API host comes from /config.json, which is
// fetched at bootstrap after this module is evaluated.
const CHAT_STREAM_PATH = "/api/ai-assistant/chat/stream";
const FEEDBACK_PATH = "/api/ai-assistant/feedback";

/**
 * Drives the assistant drawer chat against the real backend
 * (`POST /api/ai-assistant/chat/stream`, SSE) and the feedback endpoint.
 * Returns demo-shaped ChatMessage[] so the existing chat UI renders unchanged.
 */
export function useAssistantChat(ctx: AssistantChatContext) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const ctxRef = useRef(ctx);
    ctxRef.current = ctx;
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    const abortRef = useRef<AbortController | null>(null);

    // Chat demo (Super Admin → Demos): send clicks play scripted exchanges and
    // the answers "stream" from a timer — the AI backend is never called.
    const [chatDemoEnabled] = useChatDemoFlag();
    const demoRef = useRef(chatDemoEnabled);
    demoRef.current = chatDemoEnabled;
    // Which scripted exchange the next send click plays.
    const demoIndexRef = useRef(0);
    // Pending fake-stream timers, cleared on stop/reset/unmount.
    const demoTimersRef = useRef<number[]>([]);
    const clearDemoTimers = useCallback(() => {
        demoTimersRef.current.forEach(clearTimeout);
        demoTimersRef.current = [];
    }, []);
    useEffect(() => clearDemoTimers, [clearDemoTimers]);

    // Abort any in-flight stream on unmount.
    useEffect(() => () => abortRef.current?.abort(), []);

    // Merge a partial update into the message with the given id.
    const patchMessage = useCallback((id: string, patch: Partial<ChatMessage>) => {
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    }, []);

    const send = useCallback(async (raw: string) => {
        const text = raw.trim();
        if (isStreaming) return;

        // Chat demo: a send click (typically with an empty composer) plays the
        // next scripted exchange; any typed text is ignored. The answer is
        // revealed a few words per tick so it reads like a streamed reply.
        if (demoRef.current) {
            const exchange = ASSISTANT_DEMO_EXCHANGES[demoIndexRef.current];
            if (!exchange) return;
            demoIndexRef.current += 1;
            const demoUserMsg: ChatMessage = {
                id: nextId(),
                role: "user",
                kind: "user",
                text: exchange.question,
            };
            const demoBotId = nextId();
            setMessages((prev) => [
                ...prev,
                demoUserMsg,
                { id: demoBotId, role: "bot", kind: "answer", typing: true },
            ]);
            setIsStreaming(true);
            setError(null);
            const chunks = exchange.answer.match(/\S+\s*/g) ?? [exchange.answer];
            const WORDS_PER_TICK = 3;
            const TICK_MS = 45;
            const THINKING_MS = 900;
            let revealed = 0;
            const tick = () => {
                revealed = Math.min(revealed + WORDS_PER_TICK, chunks.length);
                patchMessage(demoBotId, {
                    typing: false,
                    text: chunks.slice(0, revealed).join(""),
                });
                if (revealed < chunks.length) {
                    demoTimersRef.current.push(window.setTimeout(tick, TICK_MS));
                } else {
                    setIsStreaming(false);
                }
            };
            demoTimersRef.current.push(window.setTimeout(tick, THINKING_MS));
            return;
        }

        if (!text) return;

        const userMsg: ChatMessage = { id: nextId(), role: "user", kind: "user", text };
        const botId = nextId();
        const botMsg: ChatMessage = { id: botId, role: "bot", kind: "answer", typing: true };
        setMessages((prev) => [...prev, userMsg, botMsg]);
        setIsStreaming(true);
        setError(null);

        // Map the conversation (including this new turn) to the API's
        // {role, content} shape, dropping the empty bot placeholder.
        const apiMessages = [...messagesRef.current, userMsg]
            .filter((m) => m.text)
            .map((m) => ({
                role: m.role === "bot" ? "assistant" : "user",
                content: m.text as string,
            }));

        const { projectId, analysisVersions, analysisContent, pageDescription, mode } = ctxRef.current;

        abortRef.current?.abort();
        const abort = new AbortController();
        abortRef.current = abort;

        let acc = "";
        try {
            await streamChat({
                url: geturl(CHAT_STREAM_PATH),
                body: {
                    messages: apiMessages,
                    ...(projectId ? { projectId, analysisVersions, analysisContent } : {}),
                    ...(pageDescription ? { pageDescription } : {}),
                    ...(mode && mode !== "ask" ? { mode } : {}),
                },
                callbacks: {
                    onText: (chunk) => {
                        acc += chunk;
                        patchMessage(botId, { typing: false, text: acc });
                    },
                    onFinish: (content, traceId) => {
                        patchMessage(botId, { typing: false, text: content || acc, traceId });
                        setIsStreaming(false);
                    },
                    onError: (message) => {
                        setError(message);
                        setMessages((prev) => prev.filter((m) => m.id !== botId));
                        setIsStreaming(false);
                    },
                },
                signal: abort.signal,
            });
        } catch (err: any) {
            if (err?.name !== "AbortError") {
                setError(err?.message || "Failed to send message");
                // Drop the placeholder only if nothing streamed into it.
                setMessages((prev) =>
                    prev.filter((m) => !(m.id === botId && !m.text))
                );
            }
            setIsStreaming(false);
        }
    }, [isStreaming, patchMessage]);

    const stop = useCallback(() => {
        abortRef.current?.abort();
        clearDemoTimers();
        setIsStreaming(false);
        // Keep whatever already streamed; just clear the typing indicator.
        setMessages((prev) => prev.map((m) => (m.typing ? { ...m, typing: false } : m)));
    }, [clearDemoTimers]);

    // Clear the conversation: abort any in-flight stream and go back to the
    // empty state, where the panel shows the mode's intro. The backend is
    // stateless (history is sent with each turn), so resetting the local
    // message list is all that's needed to clear context.
    const reset = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        clearDemoTimers();
        demoIndexRef.current = 0; // replay the script from the top
        setIsStreaming(false);
        setError(null);
        setMessages([]);
    }, [clearDemoTimers]);

    const sendFeedback = useCallback(
        (messageId: string, sentiment: "positive" | "negative") => {
            const target = messagesRef.current.find((m) => m.id === messageId);
            if (!target || target.feedback) return;
            patchMessage(messageId, { feedback: sentiment });
            // No trace to link the rating to (scripted demo reply) — keep it local.
            if (!target.traceId) return;
            // Fire-and-forget; backend links the rating to the trace.
            fetch(geturl(FEEDBACK_PATH), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    traceId: target.traceId,
                    sentiment,
                    comment: "",
                    pageName: ctxRef.current.pageName ?? "",
                    mode: ctxRef.current.mode ?? "ask",
                }),
            }).catch(() => {
                // Non-blocking: a failed feedback POST shouldn't disrupt the chat.
            });
        },
        [patchMessage]
    );

    return { messages, isStreaming, error, send, stop, sendFeedback, reset };
}
